import { type Application, ApplicationScheduleType } from 'api/core/generated';
import {
  AppProductType,
  LOCIZE_PAGE_TITLES_KEYS,
  LocizeNamespaces,
} from 'app-constants';
import { ConsumerLoanProduct } from 'models';
import { isConsumerLoanProduct } from 'models/guards';
import { useTranslation } from 'react-i18next';

import { useGetProductTypeByUrl } from './use-get-product-type-by-url';

const consumerLoanPageTitlesMap = {
  [ConsumerLoanProduct.SMALL_LOAN]: LOCIZE_PAGE_TITLES_KEYS.pageTitleSmallLoan,
  [ConsumerLoanProduct.FAST_LOAN]: LOCIZE_PAGE_TITLES_KEYS.pageTitleFastLoan,
  [ConsumerLoanProduct.RENOVATION_LOAN]:
    LOCIZE_PAGE_TITLES_KEYS.pageTitleRenovationLoan,
  [ConsumerLoanProduct.VEHICLE_LOAN]:
    LOCIZE_PAGE_TITLES_KEYS.pageTitleVehicleLoan,
  [ConsumerLoanProduct.TRAVEL_LOAN]:
    LOCIZE_PAGE_TITLES_KEYS.pageTitleTravelLoan,
  [ConsumerLoanProduct.HEALTH_LOAN]:
    LOCIZE_PAGE_TITLES_KEYS.pageTitleHealthLoan,
  [ConsumerLoanProduct.BEAUTY_LOAN]:
    LOCIZE_PAGE_TITLES_KEYS.pageTitleBeautyLoan,
  [ConsumerLoanProduct.EXTENSION_LOAN]:
    LOCIZE_PAGE_TITLES_KEYS.pageTitleExtensionLoan,
} as const satisfies Record<ConsumerLoanProduct, string>;

export const useGetPageTitle = (application: Application) => {
  const { t } = useTranslation(LocizeNamespaces.pageTitles);

  const productType = useGetProductTypeByUrl();

  const titleCopy = (() => {
    switch (productType) {
      case AppProductType.HIRE_PURCHASE: {
        if (!application?.id) {
          return '';
        }
        if (application.schedule_type === ApplicationScheduleType.ESTO_PAY) {
          return LOCIZE_PAGE_TITLES_KEYS.pageTitleEstoPay;
        }
        return application.free_hp_enabled
          ? LOCIZE_PAGE_TITLES_KEYS.pageTitleFreeHP
          : LOCIZE_PAGE_TITLES_KEYS.pageTitleHP;
      }
      case AppProductType.SMALL_LOAN: {
        if (!application?.id) {
          return '';
        }
        return isConsumerLoanProduct(application.schedule_type)
          ? consumerLoanPageTitlesMap[application.schedule_type]
          : '';
      }
      case AppProductType.CREDIT_LINE: {
        return LOCIZE_PAGE_TITLES_KEYS.pageTitleCL;
      }
      case AppProductType.INCOME_INSURANCE: {
        return LOCIZE_PAGE_TITLES_KEYS.pageTitleIncomeInsurance;
      }
      default:
        return '';
    }
  })();

  return t(titleCopy);
};
